import { Building2, Layers, Lock, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";

import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils/cn";

import { CheckboxField, FieldShell, FormSection, Select, Textarea } from "./controls";
import { enterpriseAddressPatch } from "../lib/address";
import { groupByCategory, groupCharacteristics } from "../lib/grouping";
import type {
  Characteristic,
  CompanySize,
  ProfileFormValues,
  Sector,
} from "../types";

const ENTERPRISE_TYPES = [
  "self_employed",
  "private",
  "cooperative",
  "not_for_profit",
  "economical_development",
  "municipality",
  "other",
] as const;

/** Fields the identification step requires (mirrors REQUIRED_FIELDS there). */
const REQUIRED_FIELDS: ReadonlySet<keyof ProfileFormValues> = new Set([
  "name",
  "enterprise_type",
  "address",
  "city",
  "zip_code",
  "phone_number",
]);

/** Uppercase micro-label shared by every field in the profile form. */
const LABEL = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

/** Cycling accent dots for the locked-value chip summaries. */
const SWATCH = [
  "bg-theme-economic-ink",
  "bg-theme-environment-ink",
  "bg-theme-social-ink",
  "bg-theme-governance-ink",
];

interface ProfileFieldsProps {
  values: ProfileFormValues;
  onChange: (patch: Partial<ProfileFormValues>) => void;
  sectors: Sector[];
  characteristics: Characteristic[];
  companySizes: CompanySize[];
  errors?: Partial<Record<keyof ProfileFormValues, string>>;
  /** When true, sectors & characteristics are read-only (diagnostic started). */
  locked?: boolean;
}

function toggle(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

/**
 * Presentational field set for the enterprise profile, grouped into titled
 * sections (identity / contact / activity) each accented with one of the four
 * sustainability hues. Fully controlled so it works both for the create form
 * (submit-on-click) and the edit form (autosave-on-change).
 */
export function ProfileFields({
  values,
  onChange,
  sectors,
  characteristics,
  companySizes,
  errors,
  locked = false,
}: ProfileFieldsProps) {
  const { t } = useTranslation();

  // Only characteristics tied to a selected sector (or tied to none = universal)
  // are relevant — mirrors the old diagnostic's sector-driven filtering. A
  // characteristic with a prerequisite is also gated until that prerequisite is
  // selected (conditional reveal).
  const selected = new Set(values.sector_ids);
  const selectedCharacteristics = new Set(values.characteristic_ids);
  const visibleCharacteristics = characteristics.filter((c) => {
    const cs = c.sector_ids ?? [];
    const sectorOk = cs.length === 0 || cs.some((id) => selected.has(id));
    if (!sectorOk) return false;
    if (c.prerequisite_id && !selectedCharacteristics.has(c.prerequisite_id)) {
      return false;
    }
    return true;
  });

  // Group visible characteristics under their main-characteristic heading, and
  // group sectors under their category header (parity with the grouped lists).
  const groupedCharacteristics = groupCharacteristics(visibleCharacteristics, t);
  const groupedSectors = groupByCategory(sectors, t);

  // Checking a characteristic just adds it; unchecking one cascades to everything
  // that (transitively) requires it (the user actively orphaned them). We never
  // strip other selections, so an orphan stored before its prerequisite existed
  // survives — matching the Django app and the delta-aware server prune.
  const toggleCharacteristic = (id: string) => {
    if (values.characteristic_ids.includes(id)) {
      const removed = characteristicAndDependents(id, characteristics);
      onChange({
        characteristic_ids: values.characteristic_ids.filter((x) => !removed.has(x)),
      });
    } else {
      onChange({ characteristic_ids: [...values.characteristic_ids, id] });
    }
  };

  // Once locked, sectors/characteristics are read-only: show a compact chip
  // summary of what's selected rather than a wall of disabled checkboxes.
  const selectedSectorNames = sectors
    .filter((s) => values.sector_ids.includes(s.id))
    .map((s) => s.name);
  const selectedCharacteristicNames = characteristics
    .filter((c) => values.characteristic_ids.includes(c.id))
    .map((c) => c.name);

  const requiredMark = (key: keyof ProfileFormValues) =>
    REQUIRED_FIELDS.has(key) ? (
      <span aria-hidden className="text-primary"> *</span>
    ) : null;

  const text = (
    key: keyof ProfileFormValues,
    labelKey: string,
    type = "text",
    // `readOnly` and `hint` used to live here for the province field, which was
    // this helper's only caller for either. Both went with it: a field the
    // member cannot edit is not a field, and the resolved values are shown in
    // the identity rail instead.
    opts?: { wrap?: string; placeholder?: string },
  ) => {
    const id = `profile-${key}`;
    const errorId = `${id}-error`;
    const error = errors?.[key];
    return (
      <div className={cn("space-y-1.5", opts?.wrap)}>
        <Label htmlFor={id} className={LABEL}>
          {t(labelKey)}
          {requiredMark(key)}
        </Label>
        {key === "address" ? (
          <AddressAutocomplete
            id={id}
            value={values[key] as string}
            required={REQUIRED_FIELDS.has(key)}
            aria-invalid={error !== undefined}
            aria-describedby={error ? errorId : undefined}
            onChange={(value) => onChange({ address: value })}
            onResolved={(result, suggestion) =>
              onChange(enterpriseAddressPatch(result, suggestion))
            }
          />
        ) : (
          <Input
            id={id}
            type={type}
            value={values[key] as string}
            placeholder={opts?.placeholder}
            required={REQUIRED_FIELDS.has(key)}
            aria-invalid={error !== undefined}
            aria-describedby={error ? errorId : undefined}
            onChange={(e) => onChange({ [key]: e.target.value })}
          />
        )}
        {error && (
          <p id={errorId} className="text-sm font-medium text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* ---- Identity ---- */}
      <FormSection
        tone="governance"
        icon={<Building2 aria-hidden />}
        title={t("questionnaire.identification.sectionIdentity")}
        description={t("questionnaire.identification.sectionIdentityHint")}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {text("name", "questionnaire.identification.name")}
          <FieldShell
            label={
              <>
                {t("questionnaire.identification.enterpriseType")}
                {requiredMark("enterprise_type")}
              </>
            }
            htmlFor="profile-enterprise_type"
            labelClassName={LABEL}
          >
            <Select
              id="profile-enterprise_type"
              value={values.enterprise_type}
              aria-invalid={errors?.enterprise_type !== undefined}
              onChange={(e) => onChange({ enterprise_type: e.target.value })}
            >
              <option value="">
                {t("questionnaire.identification.selectPlaceholder")}
              </option>
              {ENTERPRISE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`questionnaire.enterpriseTypes.${type}`)}
                </option>
              ))}
            </Select>
            {errors?.enterprise_type && (
              <p className="text-sm font-medium text-destructive">
                {errors.enterprise_type}
              </p>
            )}
          </FieldShell>
          <div className="sm:col-span-2">
            <FieldShell
              label={t("questionnaire.identification.description")}
              htmlFor="profile-description"
              labelClassName={LABEL}
            >
              <Textarea
                id="profile-description"
                value={values.description}
                aria-invalid={errors?.description !== undefined}
                onChange={(e) => onChange({ description: e.target.value })}
              />
              {errors?.description && (
                <p className="text-sm font-medium text-destructive">
                  {errors.description}
                </p>
              )}
            </FieldShell>
          </div>
        </div>
      </FormSection>

      {/* ---- Contact ---- */}
      <FormSection
        tone="economic"
        icon={<MapPin aria-hidden />}
        title={t("questionnaire.identification.sectionContact")}
        description={t("questionnaire.identification.sectionContactHint")}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {text("address", "questionnaire.identification.address", "text", {
            wrap: "sm:col-span-2",
          })}
          {text("city", "questionnaire.identification.city")}
          {/* No province field, matching the Django identification form: it is
              resolved from the address, never entered. The resolved value is
              shown in the identity rail instead. */}
          {text("zip_code", "questionnaire.identification.zipCode")}
          {text("phone_number", "questionnaire.identification.phone", "tel")}
          {text("website", "questionnaire.identification.website", "url", {
            placeholder: t("questionnaire.identification.websitePlaceholder"),
          })}
          {text("email", "questionnaire.identification.email", "email")}
        </div>
      </FormSection>

      {/* ---- Activity ---- */}
      <FormSection
        tone="environment"
        icon={<Layers aria-hidden />}
        title={t("questionnaire.identification.sectionActivity")}
        description={t("questionnaire.identification.sectionActivityHint")}
      >
        <div className="space-y-5">
          <FieldShell
            label={t("questionnaire.identification.companySize")}
            htmlFor="profile-company_size"
            labelClassName={LABEL}
          >
            <Select
              id="profile-company_size"
              className="sm:max-w-xs"
              value={values.company_size_id}
              onChange={(e) => onChange({ company_size_id: e.target.value })}
            >
              <option value="">
                {t("questionnaire.identification.selectPlaceholder")}
              </option>
              {companySizes.map((size) => (
                <option key={size.id} value={size.id}>
                  {size.name}
                </option>
              ))}
            </Select>
            {errors?.company_size_id && (
              <p className="text-sm font-medium text-destructive">
                {errors.company_size_id}
              </p>
            )}
          </FieldShell>

          <FieldShell
            label={
              <>
                {t("questionnaire.identification.sectors")}
                {/* At least one is required. The section is a `FieldShell`
                    rather than one of the `text()` fields, so it does not pick
                    the mark up from `REQUIRED_FIELDS` and carries its own. */}
                {!locked && <span aria-hidden className="text-primary"> *</span>}
              </>
            }
            description={
              locked ? undefined : t("questionnaire.identification.sectorsHint")
            }
            labelClassName={LABEL}
          >
            {locked ? (
              <ChipSummary
                items={selectedSectorNames}
                emptyLabel={t("questionnaire.profile.noneSelected")}
              />
            ) : (
              <div className="space-y-4">
                {groupedSectors.map(({ category, items }) => (
                  <div key={category}>
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {category}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {items.map((sector) => (
                        <CheckboxField
                          key={sector.id}
                          id={`sector-${sector.id}`}
                          label={sector.name}
                          checked={values.sector_ids.includes(sector.id)}
                          disabled={locked}
                          onChange={() =>
                            onChange({
                              sector_ids: toggle(values.sector_ids, sector.id),
                            })
                          }
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {errors?.sector_ids && (
              <p className="text-sm font-medium text-destructive">
                {errors.sector_ids}
              </p>
            )}
          </FieldShell>

          {(locked
            ? selectedCharacteristicNames.length > 0
            : visibleCharacteristics.length > 0) && (
            <FieldShell
              label={t("questionnaire.identification.characteristics")}
              description={
                locked
                  ? undefined
                  : t("questionnaire.identification.characteristicsHint")
              }
              labelClassName={LABEL}
            >
              {locked ? (
                <ChipSummary
                  items={selectedCharacteristicNames}
                  emptyLabel={t("questionnaire.profile.noneSelected")}
                />
              ) : (
                <div className="space-y-4">
                  {groupedCharacteristics.map((group) => (
                    <div key={group.group}>
                      <p className={cn(LABEL, "mb-2")}>{group.group}</p>
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {group.items.map((characteristic) => (
                          <CheckboxField
                            key={characteristic.id}
                            id={`characteristic-${characteristic.id}`}
                            label={characteristic.name}
                            checked={values.characteristic_ids.includes(
                              characteristic.id,
                            )}
                            disabled={locked}
                            onChange={() =>
                              toggleCharacteristic(characteristic.id)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </FieldShell>
          )}

          {/* Outside the block above on purpose: that section only renders when
              there are characteristics to show, and an error the server names
              must never depend on whether its field happened to be drawn. */}
          {errors?.characteristic_ids && (
            <p className="text-sm font-medium text-destructive">
              {errors.characteristic_ids}
            </p>
          )}

          {locked && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Lock className="size-3.5" aria-hidden />
              {t("questionnaire.identification.lockedNote")}
            </p>
          )}
        </div>
      </FormSection>
    </div>
  );
}

/** Read-only pill list of selected values (used when the profile is locked). */
function ChipSummary({ items, emptyLabel }: { items: string[]; emptyLabel: string }) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((label, i) => (
        <span
          key={label}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-secondary px-3 py-1.5 text-sm text-secondary-foreground"
        >
          <span
            aria-hidden
            className={cn("size-2 flex-none rounded-full", SWATCH[i % SWATCH.length])}
          />
          {label}
        </span>
      ))}
    </div>
  );
}

/** A characteristic id plus every characteristic that (transitively) requires it
 * — used to cascade a deselection to its dependents. */
function characteristicAndDependents(
  id: string,
  characteristics: Characteristic[],
): Set<string> {
  const removed = new Set<string>([id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const c of characteristics) {
      if (c.prerequisite_id && removed.has(c.prerequisite_id) && !removed.has(c.id)) {
        removed.add(c.id);
        changed = true;
      }
    }
  }
  return removed;
}

